import { isNil, omitBy } from 'lodash';

import type { EffectParams, ModelConfig, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import locales from '@swf/generated/locale-data.json';

import type { BoundRequest } from '../api/request';
import { FeatureFlag, ResolverTag, Site } from '../enums';

interface AlternateTag {
  hrefLang: string;
  url: string;
}

export type SeoState = AlternateTag[];

export const localeCountriesConfig: { site: Site; hreflang: string }[] = [
  { site: Site.UK, hreflang: 'en-GB' },
  { site: Site.USCO, hreflang: 'en-US' },
  { site: Site.COM, hreflang: 'en-AU' },
  { site: Site.COM, hreflang: 'en-CA' },
  { site: Site.COM, hreflang: 'en-IE' },
  { site: Site.COM, hreflang: 'en-NZ' },
  { site: Site.COM, hreflang: 'pt-BR' },
  { site: Site.COM, hreflang: 'no' },
  { site: Site.COM, hreflang: 'uk' },
  { site: Site.EU, hreflang: 'fi' },
  { site: Site.DE, hreflang: 'de-DE' },
  { site: Site.EU, hreflang: 'de-AT' },
  { site: Site.COM, hreflang: 'de-CH' },
  { site: Site.EU, hreflang: 'pl' },
  { site: Site.RU, hreflang: 'ro' },
  { site: Site.SE, hreflang: 'sv' },
  { site: Site.DK, hreflang: 'da' },
  { site: Site.ES, hreflang: 'es-ES' },
  { site: Site.COM, hreflang: 'es-AR' },
  { site: Site.COM, hreflang: 'es-MX' },
  { site: Site.COM, hreflang: 'es-CO' },
  { site: Site.COM, hreflang: 'es-CL' },
  { site: Site.COM, hreflang: 'es-VE' },
  { site: Site.COM, hreflang: 'es-UY' },
  { site: Site.COM, hreflang: 'es-CR' },
  { site: Site.COM, hreflang: 'es-PA' },
  { site: Site.COM, hreflang: 'es-PE' },
  { site: Site.FR, hreflang: 'fr' },
  { site: Site.IT, hreflang: 'it' }
];

const getDefaultAlternateLink = async (
  brand: unknown,
  request: BoundRequest,
  defaultURL: string
) => {
  try {
    const { body } = await request<{ url: string }>({
      service: 'tags.resolve',
      body: {
        tag: ResolverTag.StarsWeb,
        brand,
        site: Site.COM
      }
    });
    return { hrefLang: 'x-default', url: body.url };
  } catch (exp) {
    return { hrefLang: 'x-default', url: defaultURL };
  }
};
const effects = {
  async getAlternateLinks(payload: undefined, { dispatch, request, rootState }: EffectParams) {
    const brand = select.site.getBrand(rootState);
    const { origin } = select.location.get(rootState);

    const crossDomainAlternatesEnabled = select.feature.check(
      rootState,
      FeatureFlag.CrossDomainAlternates
    );

    if (!crossDomainAlternatesEnabled) return;

    const alternates = await Promise.allSettled([
      ...localeCountriesConfig.map(async (tag) => {
        const [locale] = tag.hreflang.split('-');
        const { body } = await request<{ url: string }>({
          service: 'tags.resolve',
          body: omitBy(
            {
              tag: ResolverTag.StarsWeb,
              brand,
              site: tag.site,
              locale: locales[locale]?.code
            },
            isNil
          )
        });

        return { hrefLang: tag.hreflang, url: body.url };
      }),
      getDefaultAlternateLink(brand, request, origin)
    ]);

    const alternateTags = alternates
      .filter((p): p is PromiseFulfilledResult<AlternateTag> => p.status === 'fulfilled')
      .map((p) => p.value);

    dispatch.seo.set(alternateTags);
  }
};

const reducers = {
  set: (state: SeoState, payload: AlternateTag[]) => payload
};

const selectors = (slice: Slicer<SeoState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  }
});

export interface SeoModel extends ModelConfig<SeoState> {
  reducers: typeof reducers;
  selectors: typeof selectors;
  effects: typeof effects;
}

const seo: SeoModel = {
  state: [],
  reducers,
  selectors,
  effects
};

export default seo;
